"use client";

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from 'gsap';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ProjectCardProps {
  project: {
    id: string;
    title: string;
    description: string;
    category: string;
    year: string;
    image?: string;
    link?: string;
    featured?: boolean;
  };
  index: number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { trackProject } = useAnalytics();

  useEffect(() => {
    const card = cardRef.current;
    const image = imageRef.current;
    const content = contentRef.current;
    
    if (!card || !image || !content) return;

    const ctx = gsap.context(() => {
      // Initial setup
      gsap.set(card, { 
        opacity: 0, 
        y: 60,
        scale: 0.95 
      });

      // Intersection Observer for scroll-triggered animation and view tracking
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Track project card view
              trackProject('view_card', {
                id: project.id,
                title: project.title,
                category: project.category,
                year: project.year,
                featured: project.featured,
              });

              gsap.to(card, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                delay: index * 0.1,
                ease: "power2.out",
              });
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(card);

      // Hover animations
      const handleMouseEnter = () => {
        gsap.to(image, {
          scale: 1.05,
          duration: 0.6,
          ease: "power2.out",
        });
        gsap.to(content, {
          y: -8,
          duration: 0.4,
          ease: "power2.out",
        });
      };

      const handleMouseLeave = () => {
        gsap.to(image, {
          scale: 1,
          duration: 0.6,
          ease: "power2.out",
        });
        gsap.to(content, {
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        });
      };

      card.addEventListener('mouseenter', handleMouseEnter);
      card.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        observer.disconnect();
        card.removeEventListener('mouseenter', handleMouseEnter);
        card.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, card);

    return () => ctx.revert();
  }, [index, project.id, project.title, project.category, project.year, project.featured, trackProject]);

  const handleClick = () => {
    // Track project card click
    trackProject('click_card', {
      id: project.id,
      title: project.title,
      category: project.category,
      year: project.year,
      featured: project.featured,
    });

    // Navigate with GSAP page transition
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        scale: 0.95,
        duration: 0.2,
        ease: "power2.out",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <article
      ref={cardRef}
      className={`group relative overflow-hidden bg-muted cursor-pointer will-change-transform ${
        project.featured ? 'lg:col-span-2 lg:row-span-2' : ''
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View project: ${project.title}`}
    >
      <Link href={`/projects/${project.id}`} className="block h-full">
        {/* Image Container */}
        <div
          ref={imageRef}
          className={`relative overflow-hidden ${
            project.featured ? 'aspect-video' : 'aspect-square'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <span className="text-gray-400 font-medium">
              {project.image || 'Project Image'}
            </span>
          </div>
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* External Link Icon */}
          {project.link && (
            <div 
              className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Track external link click
                trackProject('external_link_click', {
                  id: project.id,
                  title: project.title,
                  category: project.category,
                  year: project.year,
                  featured: project.featured,
                }, {
                  externalUrl: project.link,
                });
                window.open(project.link, '_blank', 'noopener,noreferrer');
              }}
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 cursor-pointer hover:bg-white transition-colors">
                <ExternalLink className="w-4 h-4 text-gray-700" />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className="p-6 space-y-3 will-change-transform"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              {project.category}
            </span>
            <span className="text-xs text-gray-400">
              {project.year}
            </span>
          </div>
          
          <h3 className={`font-bold text-foreground ${
            project.featured ? 'text-2xl' : 'text-lg'
          }`}>
            {project.title}
          </h3>
          
          <p className={`text-gray-600 leading-relaxed ${
            project.featured ? 'text-base' : 'text-sm'
          }`}>
            {project.description}
          </p>
          
          <div className="flex items-center gap-2 text-accent font-medium">
            <span className="text-sm">View Project</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ProjectCard;